import {useIntl} from 'react-intl'
import {useProvider} from '../../hook'
import {Form, Formik} from 'formik'
import {useUpdateProvider} from '../../context/useMutation'
import './style.scss'
import ProfessionalDetails from '../components/Detail/ProfessionalDetails'
import ContractDetails from '../components/Detail/ContractDetails'
import ServiceDetails from '../components/Detail/ServiceDetails'
import SocialMedia from '../components/Detail/SocialMedia'
import FinancialDetails from '../components/Detail/FinancialDetails'
import {InlineTable} from 'src/feature/bite/component'
import {useEffect, useState} from 'react'
import getSubCategories from 'src/feature/become-merchant/services/getSubCategories'
import getCategories from 'src/feature/become-merchant/services/getCategories'
import getPartnerTypeList from 'src/feature/become-merchant/services/getPartnerType'
import {ConnectedFocusError} from 'focus-formik-error'
import useValidationSchemaRenderer from './utils/useValidationSchemaRenderer'
import {useGetDetail} from '../../context/useQuery'

export default function ProviderDetailOverview({isLoading}: {isLoading: boolean}) {
  const {mutateAsync: updateProvider} = useUpdateProvider()
  const {refetch} = useGetDetail()
  const intl = useIntl()
  const {detail} = useProvider()
  const [editable, setEditable] = useState({
    overview: false,
    professionalDetails: false,
    serviceDetails: false,
    socialMedia: false,
    contractDetails: false,
    financialDetails: false,
  })

  const [partnerTypeList, setPartnerTypeList] = useState([])
  const [categoryList, setCategoryList] = useState([])
  const [subCategoryList, setSubCategoryList] = useState([])
  const [selectedCategoryID, setSelectedCategoryID] = useState(detail?.categoryIDs || [''])
  const {renderer} = useValidationSchemaRenderer()

  const column = [
    [
      {
        title: 'Vasco ID',
        key: 'merchantID',
        dataIndex: 'merchantID',
        tooltip:
          'A unique identifier automatically assigned to your profile within the Vasco platform.',
      },
      {
        title: 'Partner Type',
        key: 'partnerTypeID',
        dataIndex: 'partnerTypeID',
        valueSelect: true,
        tooltip:
          'A unique identifier associated with your freelancer account for internal tracking.',
        selects: partnerTypeList?.map((item: any) => ({
          value: item.id,
          text: item.name,
        })),
      },
      {
        title: 'Category',
        key: 'categoryIDs',
        dataIndex: 'categoryIDs',
        required: true,
        edit: 'multiple_select',
        tooltip: 'The primary service category that defines your area of expertise.',
        valueSelect: true,
        selects: categoryList?.map((item: any) => ({value: item.id, text: item.name})) || [],
        setSelectedCategoryID: setSelectedCategoryID,
      },
      {
        title: 'Name',
        key: 'contactName',
        tooltip: 'Your first name.',
        dataIndex: 'contactName',
        required: true,
        edit: 'input',
        maxLength: 51,
      },
      {
        title: 'Email',
        key: 'contactEmail',
        tooltip: 'Your email address used for communication and notifications.',
        required: true,
        dataIndex: 'contactEmail',
        edit: 'input',
        maxLength: 51,
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.STATUS'}),
        key: 'status',
        dataIndex: 'status',
        tooltip:
          'Indicates the current status of your profile (e.g., Active, Pending, or Inactive).',
        edit: 'select',
        status: true,
        valueSelect: true,
        selects: [
          {value: 1, text: 'Active'},
          {value: 2, text: 'Passive', disabled: true},
        ],
      },
    ],
    [
      /*       {
        title: 'Freelancer ID',
        key: 'merchantID',
        dataIndex: 'merchantID',
        tooltip:
          'A unique identifier associated with your freelancer account for internal tracking.',
      }, */
      {
        title: 'Vasco App Display Name',
        key: 'officialName',
        dataIndex: 'officialName',
        edit: 'input',
        tooltip: 'The name displayed on the Vasco app, visible to customers.',
      },
      {
        title: 'Sub-Category',
        key: 'subCategoryIDs',
        dataIndex: 'subCategoryIDs',
        required: true,
        edit: 'multiple_select',
        tooltip:
          'A more specific classification that further describes your service offerings within the selected category.',
        valueSelect: true,
        selects:
          subCategoryList?.map((item: any) => ({
            value: item.id,
            text: item.name,
            merchantCategoryName: item.merchantCategoryName,
            merchantCategoryID: item.merchantCategoryID,
          })) || [],
      },
      {
        title: 'Surname',
        key: 'contactSurname',
        required: true,
        dataIndex: 'contactSurname',
        tooltip: 'Your last name.',
        edit: 'input',
        //tooltip: intl.formatMessage({id: 'PROVIDER.SPECIALİTY_PLACEHOLDER'}),
        maxLength: 51,
      },
      {
        key: 'contactPhone',
        dataIndex: 'contactPhone',
        formName: 'contactPhone',
        required: true,
        tooltip: 'Your contact phone number.',
        title: 'Phone Number',
        edit: 'phone',
      },
      {
        title: 'Web Site',
        key: 'webSite',
        dataIndex: 'webSite',
        tooltip: 'Your professional website or portfolio link, if applicable.',
        edit: 'input',
      },
    ],
  ]

  useEffect(() => {
    getPartnerTypeList().then((res) => {
      setPartnerTypeList(res)
    })

    getCategories().then((category: any) => {
      setCategoryList(category || [])
    })
  }, [])

  useEffect(() => {
    if (categoryList.length === 0) {
      getCategories().then((category: any) => {
        setCategoryList(category || [])
      })
    }

    if (!detail?.categoryIDs) return

    getSubCategories({merchantCategoryID: detail?.categoryIDs || ['']}).then((subCategory: any) => {
      setSubCategoryList(subCategory || [])
    })
  }, [detail?.categoryIDs])

  useEffect(() => {
    if (selectedCategoryID) {
      getSubCategories({merchantCategoryID: selectedCategoryID}).then((subCategory: any) => {
        setSubCategoryList(subCategory || [])
      })
    }
  }, [selectedCategoryID])

  if (isLoading) return <div>Loading...</div>
  else
    return (
      <Formik
        initialValues={{
          id: detail?.id,
          partnerTypeID: detail?.partnerTypeID,
          categoryIDs: detail?.categoryIDs,
          subCategoryIDs: detail?.subCategoryIDs,
          contractURL: detail?.contractURL,
          name: detail?.name,
          surname: detail?.surname,
          email: detail?.email,
          phone: detail?.phone,
          status: detail?.status,
          webSite: detail?.webSite,
          mccCode: detail?.mccCode,
          contactName: detail?.contactName,
          contactSurname: detail?.contactSurname,
          contactPhone: detail?.contactPhone,
          contactEmail: detail?.contactEmail,
          slipName: detail?.slipName,
          slipSlogan: detail?.slipSlogan,
          specialty: detail?.specialty,
          education: detail?.education,
          experience: detail?.experience,
          qualifications: detail?.qualifications,
          certifications: detail?.certifications,
          certificates: detail?.certificates,
          conferencesAttended: detail?.conferencesAttended,
          memberships: detail?.memberships,
          skills: detail?.skills,
          workingHourList: detail?.workingHourList,
          facebook: detail?.facebook,
          instagram: detail?.instagram,
          linkedIn: detail?.linkedIn,
          twitter: detail?.twitter,
          bankName: detail?.bankName,
          iban: detail?.iban,
          taxOffice: detail?.taxOffice,
          taxNumber: detail?.taxNumber,
          invoiceAddress: detail?.invoiceAddress,
          imagesData: detail?.imagesData,
          onlineClass: detail?.onlineClass,
          groupClass: detail?.groupClass,
          contractDate: detail?.contractDate,
          contractDuration: detail?.contractDuration,
          contractDocument: detail?.contractDocument,
          onboardingDate: detail?.onboardingDate,
          payoutDate: detail?.payoutDate,
          payoutMethod: detail?.payoutMethod,
          currency: detail?.currency,
          paymentFrequency: detail?.paymentFrequency,
          vascoAppDisplayName: detail?.vascoAppDisplayName,
          infoContentData: detail?.infoContentData,
          additionalInformations: detail?.additionalInformations,
          officialName: detail?.officialName,
          financialTaxOffice: detail?.financialTaxOffice,
          financialIBAN: detail?.financialIBAN,
          financialTaxNumber: detail?.financialTaxNumber,
          financialInvoiceAddress: detail?.financialInvoiceAddress,
          financialBankName: detail?.financialBankName,
          financialOfficialName: detail?.financialOfficialName,
          financialCurrency: detail?.financialCurrency,
          financialPayoutDate: detail?.financialPayoutDate,
          financialPayoutMethod: detail?.financialPayoutMethod,
          financialPaymentFrequency: detail?.financialPaymentFrequency,
        }}
        validate={async (values) => {
          const schema = renderer(editable)

          const errors: any = {}
          try {
            await schema.validate(values, {abortEarly: false})
          } catch (err: any) {
            if (err.inner && err.inner.length > 0) {
              err.inner.forEach((error: any) => {
                if (!errors[error.path]) {
                  errors[error.path] = error.message
                }
              })
            } else {
              errors[err.path] = err.message
            }
          }
          return errors
        }}
        onSubmit={async (values, {resetForm}) => {
          const updateResponse = await updateProvider(values)

          if (updateResponse) {
            const res = (await refetch()) as any
            if (res?.error) {
              return
            }

            resetForm({
              values: {
                ...values,
                additionalInformations: res?.data?.additionalInformations,
                infoContentData: res?.data?.infoContentData,
              },
            })
            setEditable({
              overview: false,
              professionalDetails: false,
              serviceDetails: false,
              socialMedia: false,
              contractDetails: false,
              financialDetails: false,
            })
          }
        }}
      >
        {({values, resetForm, handleSubmit, errors}: any) => {
          return (
            <Form>
              <ConnectedFocusError />
              <div className='card mb-5 mb-xl-10'>
                <div className='card-header'>
                  <div className='card-title m-0 d-flex justify-content-between w-100'>
                    <div>
                      <h3 className='fw-bold mb-2 mt-5'>
                        {intl.formatMessage({id: 'MEMBER.DETAIL.OVERVIEW'})}
                      </h3>
                      <p className='text-muted fw-normal fs-6'>
                        Provide details about your business to ensure accurate representation on the
                        platform.
                      </p>
                    </div>
                  </div>
                </div>
                <div className='card-body p-9'>
                  <InlineTable
                    loading={isLoading}
                    dataSource={detail}
                    columns={column}
                    editable={editable.overview}
                    values={values}
                  />
                </div>
                <div className='card-footer d-flex w-100 justify-content-end'>
                  {editable.overview ? (
                    <div>
                      <button
                        className='btn btn-primary'
                        style={{marginRight: 10}}
                        onClick={() => {
                          setEditable({
                            overview: false,
                            professionalDetails: false,
                            serviceDetails: false,
                            socialMedia: false,
                            contractDetails: false,
                            financialDetails: false,
                          })
                          resetForm()
                        }}
                      >
                        <i className='bi bi-x me-2'></i>
                        {intl.formatMessage({id: 'GLOBAL.BUTTON.CANCEL'})}
                      </button>
                      <button
                        className='btn btn-primary'
                        disabled={isLoading}
                        type='button'
                        onClick={async () => {
                          await handleSubmit()
                        }}
                      >
                        <i className='bi bi-save2 me-2'></i>
                        {isLoading
                          ? intl.formatMessage({id: 'GLOBAL.BUTTON.SAVING'})
                          : intl.formatMessage({id: 'GLOBAL.BUTTON.SAVE'})}
                      </button>
                    </div>
                  ) : (
                    <button
                      className='btn btn-primary'
                      onClick={() => {
                        resetForm()
                        setEditable({
                          overview: true,
                          professionalDetails: false,
                          serviceDetails: false,
                          socialMedia: false,
                          contractDetails: false,
                          financialDetails: false,
                        })
                      }}
                    >
                      <i className='bi bi-pencil me-2'></i>
                      {intl.formatMessage({id: 'GLOBAL.BUTTON.EDIT'})}
                    </button>
                  )}
                </div>
              </div>

              <ProfessionalDetails
                intl={intl}
                loading={isLoading}
                editable={editable}
                setEditable={setEditable}
              />

              <ServiceDetails
                intl={intl}
                loading={isLoading}
                editable={editable}
                setEditable={setEditable}
              />

              <SocialMedia
                loading={isLoading}
                intl={intl}
                editable={editable}
                setEditable={setEditable}
              />

              <ContractDetails
                loading={isLoading}
                intl={intl}
                editable={editable}
                setEditable={setEditable}
              />

              <FinancialDetails
                loading={isLoading}
                intl={intl}
                editable={editable}
                setEditable={setEditable}
              />
            </Form>
          )
        }}
      </Formik>
    )
}
